import { Link } from "react-router-dom";
import { IContactDetails, IEvent } from "../../../interfaces/models/IEvent";
import { ReactComponent as EmailIcon } from "../../../../src/assets/images&icons/email.svg";
import DDMultiple, {
  Option,
} from "../../../components/Atomos/DropDown/DDMultiple";
import { fetchPersonByFirm, fetchPersonByFirmIds, getPersonById } from "../../../Services/Person";
import { useEffect, useState } from "react";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import { updateEvent, getEventById } from "../../../Services/Event";
import { ModifyEvent, NewEvent } from "../../../redux/states/event.state";
import { useDispatch } from "react-redux";
import { getFirmContactByFirmId } from "../../../Services/FirmContact";
import { getWSGFirmIds } from "../../../Services/Firm";
import { ContactTypeString } from "../../../components/Catalogs/FirmContact/FirmContactEnums";
import MemberEventsContactEdit from "./MemberEventsContactEdit";
import ProfilePhoto from "../../../components/Atomos/ProfilePhoto";
import { resolveProfilePictureDisplayUrl } from "../../../utils/profilePictureUrl";
import { formatPhoneForDisplay } from "../../../utils/formatPhoneForDisplay";
import {
  buildFirmProfilePath,
  buildPersonProfilePath,
} from "../../../utils/publicProfileUrls";

export default function StaffContactUsTab({
  event,
  isEditMode,
  onEditClick,
  onEventUpdate,
}: {
  event: IEvent | null;
  isEditMode: boolean;
  onEditClick: () => void;
  onEventUpdate?: (updatedEvent: IEvent) => void;
}) {
  const dispatch = useDispatch();
  const [contacts, setContacts] = useState<Option[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Option[]>([]);
  const [professionals, setProfessionals] = useState<Option[]>([]);
  const [selectedProfessionals, setSelectedProfessionals] = useState<Option[]>([]);
  const [wsgContacts, setWsgContacts] = useState<Option[]>([]);
  const [selectedWSGContacts, setSelectedWSGContacts] = useState<Option[]>([]);
  
  useEffect(() => {
    if (!isEditMode) {
      return;
    }
    const loadContacts = async () => {
      if (!event) {
        return;
      }

      if (event.memberEventsTab) {
        const memberSponsors = event.sponsorHost?.filter(
          (sponsor: any) => sponsor.type === "member"
        ) || [];
        
        if (memberSponsors.length === 0) {
          setProfessionals([]);
          setWsgContacts([]);
          setSelectedProfessionals([]);
          setSelectedWSGContacts([]);
          return;
        }

        const firmId = memberSponsors[0].id;
        
        if (!firmId) {
          setProfessionals([]);
          setWsgContacts([]);
          setSelectedProfessionals([]);
          setSelectedWSGContacts([]);
          return;
        }

        try {
          const professionalsList = await fetchPersonByFirm(firmId);
          const professionalsOptions = professionalsList
            .filter((person: any) => person.firm === firmId)
            .map((person: any) => ({
              id: person.id,
              name: `${person.name || ""} ${person.lastName || ""}`.trim() || "Unknown",
            }));
          setProfessionals(professionalsOptions);

          const firmContactsResponse = await getFirmContactByFirmId(firmId);
          
          let firmContacts: any[] = [];
          if (Array.isArray(firmContactsResponse)) {
            firmContacts = firmContactsResponse;
          } else if (firmContactsResponse) {
            firmContacts = [firmContactsResponse];
          }
          
          const wsgContactsOptions: Option[] = [];
          const uniqueContactIds = new Set<string>();
          
          for (const firmContact of firmContacts) {
            if (!firmContact) {
              continue;
            }
            
            if (firmContact.firm !== firmId) {
              continue;
            }
            
            if (firmContact.level === true) {
              if (firmContact.contacts && Array.isArray(firmContact.contacts) && firmContact.contacts.length > 0) {
                for (const contact of firmContact.contacts) {
                  if (!contact || !contact.contact) {
                    continue;
                  }
                  if (uniqueContactIds.has(contact.contact)) {
                    continue;
                  }
                  
                  uniqueContactIds.add(contact.contact);
                  
                  try {
                    const person = await getPersonById(contact.contact);
                    if (person && person.id) {
                      const contactTypeLabel = ContactTypeString[contact.type as keyof typeof ContactTypeString] || contact.type;
                      const personName = `${person.name || ""} ${person.lastName || ""}`.trim() || "Unknown";
                      wsgContactsOptions.push({
                        id: person.id,
                        name: `${contactTypeLabel} - ${personName}`,
                      });
                    }
                  } catch (error) {
                  }
                }
              }
            } else {
              if (firmContact.jurisdictions && Array.isArray(firmContact.jurisdictions) && firmContact.jurisdictions.length > 0) {
                for (const jurisdiction of firmContact.jurisdictions) {
                  if (!jurisdiction || !jurisdiction.contacts || !Array.isArray(jurisdiction.contacts)) {
                    continue;
                  }
                  
                  for (const contact of jurisdiction.contacts) {
                    if (!contact || !contact.contact) {
                      continue;
                    }
                    if (uniqueContactIds.has(contact.contact)) {
                      continue;
                    }
                    
                    uniqueContactIds.add(contact.contact);
                    
                    try {
                      const person = await getPersonById(contact.contact);
                      if (person && person.id) {
                        const contactTypeLabel = ContactTypeString[contact.type as keyof typeof ContactTypeString] || contact.type;
                        const personName = `${person.name || ""} ${person.lastName || ""}`.trim() || "Unknown";
                        wsgContactsOptions.push({
                          id: person.id,
                          name: `${contactTypeLabel} - ${personName}`,
                        });
                      }
                    } catch (error) {
                    }
                  }
                }
              }
            }
          }
          
          setWsgContacts(wsgContactsOptions);

          if (event.contactUs && Array.isArray(event.contactUs)) {
            const selectedProfs = professionalsOptions.filter((prof: Option) =>
              prof.id && event.contactUs?.includes(prof.id)
            );
            setSelectedProfessionals(selectedProfs);

            const selectedWSG = wsgContactsOptions.filter((contact: Option) =>
              contact.id && event.contactUs?.includes(contact.id)
            );
            setSelectedWSGContacts(selectedWSG);
          } else {
            setSelectedProfessionals([]);
            setSelectedWSGContacts([]);
          }
        } catch (error) {
          setProfessionals([]);
          setWsgContacts([]);
        }
      } else {
        const wsgFirmIds = await getWSGFirmIds();
        const contactsResponse = await fetchPersonByFirmIds(wsgFirmIds);
        const contactsList = contactsResponse.map((contact: any) => ({
          id: contact.id,
          name: `${contact.name || ""} ${contact.lastName || ""}`.trim() || "Unknown",
        }));
        setContacts(contactsList);
        
        if (event.contactUs && Array.isArray(event.contactUs)) {
          const selected = contactsList.filter(
            (c: Option) => c.id && event.contactUs?.includes(c.id)
          );
          setSelectedContacts(selected);
        } else {
          setSelectedContacts([]);
        }
      }
    };
    loadContacts();
  }, [
    isEditMode,
    event?.id,
    event?.memberEventsTab,
    (event?.sponsorHost || []).map((s) => `${s.type}:${s.id}`).join("|"),
  ]);

  const handleContactsChange = (contacts: any) => {
    setSelectedContacts(contacts);
  };

  const handleProfessionalsChange = (professionals: any) => {
    setSelectedProfessionals(professionals);
  };

  const handleWSGContactsChange = (contacts: any) => {
    setSelectedWSGContacts(contacts);
  };

  const handleSaveContacts = async () => {
    if (event && event.id) {
      let contactIds: string[] = [];
      
      if (event.memberEventsTab) {
        const professionalIds = selectedProfessionals.map((prof: any) => prof.id);
        const wsgContactIds = selectedWSGContacts.map((contact: any) => contact.id);
        contactIds = [...professionalIds, ...wsgContactIds];
      } else {
        contactIds = selectedContacts.map((contact: any) => contact.id);
      }
      
      const { contactUsDetails: _omit, ...eventWithoutDetails } = event;
      const updatedEvent = await updateEvent({
        ...eventWithoutDetails,
        contactUs: contactIds,
      });
      
      const reloadedEvent = await getEventById(event.id);
      
      if (reloadedEvent) {
        dispatch(NewEvent(reloadedEvent));
        if (onEventUpdate) {
          onEventUpdate(reloadedEvent);
        }
      } else {
        dispatch(ModifyEvent({ 
          contactUs: updatedEvent.contactUs
        }));
      }
      onEditClick();
    }
  };

  if (!event) {
    return <div>Event not found</div>;
  }

  if (isEditMode) {
    if (event.memberEventsTab) {
      return (
        <MemberEventsContactEdit
          professionals={professionals}
          selectedProfessionals={selectedProfessionals}
          wsgContacts={wsgContacts}
          selectedWSGContacts={selectedWSGContacts}
          onProfessionalsChange={handleProfessionalsChange}
          onWSGContactsChange={handleWSGContactsChange}
          onSave={handleSaveContacts}
        />
      );
    }

    return (
      <div className="bg-[#F6F6F6] pb-4 min-h-[500px]">
        <p className="text-start font-Roboto text-[#474F56] font-[400] text-lg ml-5 pt-4">
          Contacts
        </p>
        <div className="h-px bg-primary w-[200px] mb-4"></div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-2 mx-5">
          <DDMultiple
            title=""
            objects={contacts}
            selectedObjst={selectedContacts}
            seter={(e) => handleContactsChange(e)}
            ddclass="w-full md:w-1/2"
          />
          <LegacyButton
            text="Save"
            onClick={handleSaveContacts}
            className="w-full md:w-1/3 mt-4 md:mt-0"
          />
        </div>
      </div>
    );
  }
  return (
    <div className="bg-[#F6F6F6] pb-4">
      <p className="text-start font-Roboto text-[#474F56] font-[400] text-lg ml-5 pt-4">
        Contacts
      </p>
      <div className="h-px bg-primary w-[200px] mb-4"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-5">
        {event.contactUsDetails?.map((contact: IContactDetails) => {
          const contactPhotoSrc = contact.id
            ? resolveProfilePictureDisplayUrl(contact.id, contact.photo)
            : "";
          const personPath = buildPersonProfilePath(
            contact.name,
            "",
            contact.personLegacyId
          );
          const firmPath = buildFirmProfilePath(
            contact.firmName,
            contact.firmShortId || contact.firmLegacyId
          );
          return (
          <div key={contact.id} className="flex items-start">
          <div className="w-16 h-16 xs:w-20 xs:h-20 sm:w-24 sm:h-24 md:w-[90px] md:h-[90px] rounded-full mr-3 xs:mr-4 sm:mr-5 md:mr-6 flex-shrink-0 overflow-hidden flex items-center justify-center">
            <ProfilePhoto
              src={contactPhotoSrc}
              alt={contact.name}
              imgClassName="w-full h-full object-cover object-[center_25%]"
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-0">
              {personPath ? (
                <Link
                  to={personPath}
                  className="font-Roboto font-bold text-gray-font text-[18px] md:text-[20px] hover:text-red-600 hover:underline transition-colors text-left"
                >
                  {contact.name}
                </Link>
              ) : (
                <span className="font-Roboto font-bold text-gray-font text-[18px] md:text-[20px] text-left">
                  {contact.name}
                </span>
              )}
            </div>
            <p className="font-Roboto font-normal text-[#474F56] text-[16px] md:text-[17px] lg:text-[18px] mb-2 text-left italic">
              {contact.title || "Partner"}
            </p>

              <div className="flex flex-row items-center justify-center gap-2">
                <p className="font-Roboto font-[350] text-[#474F56] text-[16px] md:text-[17px] lg:text-[18px] text-left">
                  {formatPhoneForDisplay(contact.phone, { includeBasecode: false }) ||
                    "No phone number"}
                </p>
                <span className="w-0.5 h-4 bg-gray-300 inline-block"></span>
                <button
                  onClick={() =>
                    window.open(`mailto:${contact.email}`, "_blank")
                  }
                  className="flex items-center gap-2 group"
                  title={`Email ${contact.name}`}
                >
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full group-hover:bg-red-600 transition-colors flex items-center justify-center">
                    <EmailIcon className="w-4 h-4" />
                  </div>
                  <p className="font-Roboto font-normal text-[#474F56] text-[16px] md:text-[17px] lg:text-[18px] text-left group-hover:text-red-600 transition-colors cursor-pointer">
                    Email
                  </p>
                </button>
              </div>
            {contact.firmName &&
              (firmPath ? (
                <Link
                  to={firmPath}
                  className="font-Roboto font-[350] text-[#0070CD] text-[16px] md:text-[17px] lg:text-[18px] hover:text-red-600 hover:underline transition-colors text-left block"
                >
                  {contact.firmName}
                </Link>
              ) : (
                <span className="font-Roboto font-[350] text-[#474F56] text-[16px] md:text-[17px] lg:text-[18px] text-left block">
                  {contact.firmName}
                </span>
              ))}
            {contact.countryName || contact.stateName ? (
            <div className="flex flex-row items-center gap-2">
              <p className="font-Roboto font-normal text-[#474F56] text-[16px] md:text-[17px] lg:text-[18px] mb-2 text-left italic">
                {contact.countryName}
              </p>
              {contact.countryName && contact.stateName && <span className="text-[#474F56] text-[16px] md:text-[17px] lg:text-[18px] mb-2 text-left italic">
                -
              </span>}
              <p className="font-Roboto font-normal text-[#474F56] text-[16px] md:text-[17px] lg:text-[18px] mb-2 text-left italic">
                {contact.stateName}
              </p>
            </div>
            ): <p className="font-Roboto font-normal text-[#474F56] text-[16px] md:text-[17px] lg:text-[18px] mb-2 text-left italic">
                Texas - USA
              </p>}
          </div>
        </div>
        );
        })}
      </div>
    </div>
  );
}
